import { FC, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "common/components/base/select/Select";
import {
  knowledgeLibraryFilterOptions,
  toTitleCase
} from "modules/knowledge-library/data/config/knowledge-library.config";
import {
  GraphNode,
  GraphEdge,
  NodeType,
  NodeProperties,
  RelationshipType
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import { Tab, Tabs } from "@mui/material";
import { Description } from "@mui/icons-material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import blueprintPlaceholder from "assets/media/images/Ai-search/blueprint-placeholder.png";
import GraphCanvas from "modules/knowledge-library/pages/knowledge-library/components/graph-canvas/GraphCanvas";
import PropertiesPanel, {
  EntityIcon,
  getEntityIconColor,
  getEntityBgColor
} from "modules/knowledge-library/pages/knowledge-library/components/PropertiesPanel";
import { KnowledgeLibraryService } from "modules/knowledge-library/data/services/knowledge-library.service";
import SearchLoader from "modules/knowledge-library/pages/knowledge-library/components/search-loader/SearchLoader";
import "./ViewData.scss";

const ViewData: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const kgFilterResponse = (location.state as any)?.kgFilterResponse;
  const keywordsApplied = kgFilterResponse?.data?.keywords_applied;

  const initialAsset = keywordsApplied?.asset
    ? toTitleCase(keywordsApplied.asset)
    : "FPSO";
  const initialEquipmentType = keywordsApplied?.equipment_type
    ? toTitleCase(keywordsApplied.equipment_type)
    : "Compressor";
  const initialDiscipline = keywordsApplied?.discipline
    ? toTitleCase(keywordsApplied.discipline)
    : "Mechanical";

  const [asset, setAsset] = useState<string>(initialAsset);
  const [equipmentType, setEquipmentType] =
    useState<string>(initialEquipmentType);
  const [discipline, setDiscipline] = useState<string>(initialDiscipline);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const [lastSearched, setLastSearched] = useState({
    asset: initialAsset,
    equipmentType: initialEquipmentType,
    discipline: initialDiscipline
  });

  const isSearchDisabled =
    asset === lastSearched.asset &&
    equipmentType === lastSearched.equipmentType &&
    discipline === lastSearched.discipline;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const retrievedNodes = kgFilterResponse?.data?.retrieved_nodes;

  const { nodes, edges, properties, connectedEntities, nodePropertiesMap } =
    useMemo(() => {
      if (
        !retrievedNodes ||
        !Array.isArray(retrievedNodes) ||
        retrievedNodes.length === 0
      ) {
        return {
          nodes: [],
          edges: [],
          properties: null,
          connectedEntities: [],
          nodePropertiesMap: {} as Record<string, NodeProperties>
        };
      }

      const labelToNodeType = (label: string): NodeType => {
        const l = label?.toLowerCase() || "";
        if (l === "asset") return NodeType.ASSET;
        if (l === "system") return NodeType.SYSTEM;
        if (l === "subsystem") return NodeType.SUBSYSTEM;
        if (l === "equipment") return NodeType.EQUIPMENT;
        if (l === "requirement") return NodeType.REQUIREMENT;
        if (l === "discipline") return NodeType.DISCIPLINE;
        if (l === "document") return NodeType.DOCUMENT;
        if (l === "component") return NodeType.COMPONENT;
        if (l === "sensor") return NodeType.SENSOR;
        return NodeType.SYSTEM;
      };

      const mapRelationship = (rel: unknown): RelationshipType => {
        const r = (typeof rel === "string" ? rel : "").toUpperCase();
        if (r === "CONTAINS") return RelationshipType.CONTAINS;
        if (r === "OWNED_BY") return RelationshipType.OWNED_BY;
        if (r === "HAS") return RelationshipType.HAS;
        if (r === "CONSTRAINT") return RelationshipType.CONSTRAINT;
        if (r.includes("COMPONENT")) return RelationshipType.HAS_COMPONENT;
        if (r.includes("REQUIREMENT")) return RelationshipType.HAS_REQUIREMENT;
        if (r.includes("SOURCED") || r.includes("DOCUMENT"))
          return RelationshipType.SOURCED_FROM;
        if (r.includes("MONITOR") || r.includes("SENSOR"))
          return RelationshipType.MONITORED_BY;
        return RelationshipType.CONTAINS;
      };

      const getDisplayName = (item: any): string => {
        if (item.name && item.name.trim()) return item.name;
        if (item.properties?.type) return item.properties.type;
        if (item.properties?.name) return item.properties.name;
        return item.label;
      };

      // Deduplicate nodes by label + displayName
      const uniqueMap = new Map<string, any>();
      retrievedNodes.forEach((item: any) => {
        const displayName = getDisplayName(item);
        const key = `${item.label}-${displayName}`;
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, { ...item, _displayName: displayName });
        }
      });
      const uniqueItems = Array.from(uniqueMap.values());

      const COL_GAP = 350;
      const ROW_GAP = 250;
      const START_X = 300;
      const START_Y = 150;

      // Hierarchy level for determining CONTAINS direction
      const typeHierarchy = (type: NodeType): number => {
        switch (type) {
          case NodeType.ASSET:
            return 0;
          case NodeType.SYSTEM:
            return 1;
          case NodeType.SUBSYSTEM:
            return 2;
          case NodeType.EQUIPMENT:
            return 3;
          case NodeType.COMPONENT:
            return 4;
          default:
            return 5;
        }
      };

      // Build a name-to-index lookup for matching related_to references
      const nameToIndex = new Map<string, number>();
      uniqueItems.forEach((item: any, i: number) => {
        const displayName = item._displayName;
        nameToIndex.set(displayName, i);
        if (item.name) nameToIndex.set(item.name, i);
      });

      // Build node types array for hierarchy comparison
      const nodeTypes: NodeType[] = uniqueItems.map((item: any) =>
        labelToNodeType(item.label)
      );

      // Build edges — iterate over ALL retrievedNodes (including duplicates)
      // so that one node with multiple relationships creates multiple edges
      const allEdges: GraphEdge[] = [];
      const edgeKeySet = new Set<string>();
      let edgeIdx = 0;

      retrievedNodes.forEach((item: any) => {
        if (!item.related_to || !item.relationship) return;
        const rel = mapRelationship(item.relationship);

        // Find source node index from unique items via name lookup
        const displayName = getDisplayName(item);
        const sourceIdx =
          nameToIndex.get(displayName) ?? nameToIndex.get(item.name);
        if (sourceIdx === undefined) return;

        // Find target node index
        const targetIdx = nameToIndex.get(item.related_to);
        if (targetIdx === undefined) return;

        // Arrow always goes from current node to related_to node
        const source = `node-${sourceIdx}`;
        const target = `node-${targetIdx}`;

        // Avoid duplicate edges (include relationship type in key)
        const edgeKey = `${source}-${target}-${rel}`;
        const reverseKey = `${target}-${source}-${rel}`;
        if (!edgeKeySet.has(edgeKey) && !edgeKeySet.has(reverseKey)) {
          edgeKeySet.add(edgeKey);
          allEdges.push({
            id: `edge-${edgeIdx++}`,
            source,
            target,
            relationship: rel
          });
        }
      });

      // Identify CONTAINS chain for top row
      const containsEdges = allEdges.filter(
        (e) => e.relationship === RelationshipType.CONTAINS
      );

      // Build adjacency for CONTAINS: parent → children
      const containsChildren = new Map<number, number[]>();
      containsEdges.forEach((e) => {
        const src = parseInt(e.source.replace("node-", ""));
        const tgt = parseInt(e.target.replace("node-", ""));
        if (!containsChildren.has(src)) containsChildren.set(src, []);
        containsChildren.get(src)!.push(tgt);
      });

      // Find root: node that is a CONTAINS source but never a target
      const containsTargetSet = new Set(
        containsEdges.map((e) => parseInt(e.target.replace("node-", "")))
      );
      const containsSourceSet = new Set(
        containsEdges.map((e) => parseInt(e.source.replace("node-", "")))
      );
      let rootIdx = -1;
      containsSourceSet.forEach((s) => {
        if (!containsTargetSet.has(s)) rootIdx = s;
      });

      // Walk CONTAINS chain (linear path from root following hierarchy)
      const containsChain: number[] = [];
      if (rootIdx !== -1) {
        let current: number | undefined = rootIdx;
        while (current !== undefined) {
          containsChain.push(current);
          const children: number[] = containsChildren.get(current) || [];
          current =
            children.length > 0
              ? children.sort(
                  (a: number, b: number) =>
                    typeHierarchy(nodeTypes[a]) - typeHierarchy(nodeTypes[b])
                )[0]
              : undefined;
        }
      }

      // Build parent-child map for HAS/OWNED_BY edges (parent in CONTAINS chain → children below)
      const hasChildren = new Map<number, number[]>();
      const nonContainsEdges = allEdges.filter(
        (e) => e.relationship !== RelationshipType.CONTAINS
      );

      // For each non-CONTAINS edge where source is in CONTAINS chain, group target as child
      nonContainsEdges.forEach((e) => {
        const srcIdx = parseInt(e.source.replace("node-", ""));
        const tgtIdx = parseInt(e.target.replace("node-", ""));
        if (containsChain.includes(srcIdx) && !containsChain.includes(tgtIdx)) {
          if (!hasChildren.has(srcIdx)) hasChildren.set(srcIdx, []);
          if (!hasChildren.get(srcIdx)!.includes(tgtIdx)) {
            hasChildren.get(srcIdx)!.push(tgtIdx);
          }
        }
      });

      // Collect all placed node indices
      const placedSet = new Set<number>(containsChain);
      hasChildren.forEach((children) =>
        children.forEach((c) => placedSet.add(c))
      );

      // Find remaining nodes not yet placed (e.g., Discipline connected via OWNED_BY from a requirement)
      const remainingIndices: number[] = [];
      uniqueItems.forEach((_: any, i: number) => {
        if (!placedSet.has(i)) remainingIndices.push(i);
      });

      // Try to attach remaining nodes to their connected parent in second row
      const thirdRowParent = new Map<number, number>(); // remaining node → its parent in row2
      remainingIndices.forEach((ri) => {
        for (const e of nonContainsEdges) {
          const srcIdx = parseInt(e.source.replace("node-", ""));
          const tgtIdx = parseInt(e.target.replace("node-", ""));
          if (srcIdx === ri && placedSet.has(tgtIdx)) {
            thirdRowParent.set(ri, tgtIdx);
            break;
          }
          if (tgtIdx === ri && placedSet.has(srcIdx)) {
            thirdRowParent.set(ri, srcIdx);
            break;
          }
        }
      });

      // --- Position nodes in a tree layout ---
      const nodePositions: Record<number, { x: number; y: number }> = {};

      // Row 0: CONTAINS chain (all nodes, no limit)
      containsChain.forEach((idx, col) => {
        nodePositions[idx] = {
          x: START_X + col * COL_GAP,
          y: START_Y
        };
      });

      // Row 1: HAS children positioned below their parent
      // If a parent has multiple children, spread them horizontally centered below
      const CHILD_GAP = 280;
      containsChain.forEach((parentIdx) => {
        const children = hasChildren.get(parentIdx) || [];
        if (children.length === 0) return;
        const parentX = nodePositions[parentIdx].x;
        const totalWidth = (children.length - 1) * CHILD_GAP;
        const startChildX = parentX - totalWidth / 2;
        children.forEach((childIdx, ci) => {
          nodePositions[childIdx] = {
            x: startChildX + ci * CHILD_GAP,
            y: START_Y + ROW_GAP
          };
        });
      });

      // Row 2: Remaining nodes (e.g., Discipline) below their connected node
      remainingIndices.forEach((ri, idx) => {
        const parentIdx = thirdRowParent.get(ri);
        if (parentIdx !== undefined && nodePositions[parentIdx]) {
          nodePositions[ri] = {
            x: nodePositions[parentIdx].x,
            y: nodePositions[parentIdx].y + ROW_GAP
          };
        } else {
          // Fallback: place at end of row 2
          nodePositions[ri] = {
            x: START_X + idx * COL_GAP,
            y: START_Y + ROW_GAP * 2
          };
        }
      });

      // Resolve overlaps in row 1: shift nodes that are too close
      const row1Nodes = Array.from(placedSet).filter(
        (idx) =>
          !containsChain.includes(idx) &&
          nodePositions[idx]?.y === START_Y + ROW_GAP
      );
      row1Nodes.sort((a, b) => nodePositions[a].x - nodePositions[b].x);
      for (let i = 1; i < row1Nodes.length; i++) {
        const prev = nodePositions[row1Nodes[i - 1]];
        const curr = nodePositions[row1Nodes[i]];
        if (curr.x - prev.x < CHILD_GAP) {
          curr.x = prev.x + CHILD_GAP;
        }
      }

      const allNodes: GraphNode[] = uniqueItems.map((item: any, i: number) => {
        const pos = nodePositions[i] || { x: START_X, y: START_Y };
        return {
          id: `node-${i}`,
          label: item._displayName,
          type: labelToNodeType(item.label),
          x: pos.x,
          y: pos.y,
          propsCount: item.properties ? Object.keys(item.properties).length : 0
        };
      });

      const graphProperties: NodeProperties = {
        discipline: initialDiscipline,
        status: "Retrieved",
        criticality: "\u2014",
        lastUpdated: new Date().toISOString().split("T")[0]
      };

      // Build a map of node ID -> properties from API response
      const propsMap: Record<string, NodeProperties> = {};
      uniqueItems.forEach((item: any, i: number) => {
        if (item.properties && typeof item.properties === "object") {
          const nodeProps: NodeProperties = {};
          Object.entries(item.properties).forEach(([key, val]) => {
            if (
              val !== null &&
              val !== undefined &&
              String(val).trim() !== ""
            ) {
              nodeProps[key] = String(val);
            }
          });
          propsMap[`node-${i}`] = nodeProps;
        }
      });

      const graphConnectedEntities = uniqueItems.map(
        (item: any, i: number) => ({
          id: `ce-${i}`,
          name: item._displayName,
          category: (item.label || "").toUpperCase(),
          relationship: mapRelationship(item.relationship),
          type: labelToNodeType(item.label)
        })
      );

      return {
        nodes: allNodes,
        edges: allEdges,
        properties: graphProperties,
        connectedEntities: graphConnectedEntities,
        nodePropertiesMap: propsMap
      };
    }, [retrievedNodes, initialAsset, initialEquipmentType, initialDiscipline]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSearch = async () => {
    const request = {
      asset: asset.toLowerCase(),
      equipment_type: equipmentType.toLowerCase(),
      discipline: discipline.toLowerCase()
    };
    setIsLoading(true);
    try {
      const response = await KnowledgeLibraryService.kgFilter(request);
      setLastSearched({ asset, equipmentType, discipline });
      navigate("/knowledge-library/view-data", {
        state: { kgFilterResponse: response },
        replace: true
      });
    } catch (error) {
      console.error("kg-filter error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* <HeaderNew showSearch={false} /> */}
      <div className="view-data">
        <div className="view-data__content">
          {/* Breadcrumb */}
          <div className="view-data__breadcrumb">
            <span
              className="view-data__breadcrumb-link"
              onClick={() => navigate("/")}
            >
              Home
            </span>
            <span className="view-data__breadcrumb-separator">&gt;</span>
            <span
              className="view-data__breadcrumb-link"
              onClick={() => navigate("/knowledge-library")}
            >
              Knowledge Library
            </span>
            <span className="view-data__breadcrumb-separator">&gt;</span>
            <span className="view-data__breadcrumb-current">Search result</span>
          </div>

          {/* Filters */}
          <div className="view-data__filters">
            <div className="view-data__filter-row">
              <div className="view-data__filter-group">
                <label className="view-data__filter-label">Asset</label>
                <Select
                  options={[
                    ...new Set([
                      ...knowledgeLibraryFilterOptions.assets.filter(
                        (o) => o !== "Select the Asset"
                      ),
                      initialAsset
                    ])
                  ]}
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <div className="view-data__filter-group">
                <label className="view-data__filter-label">
                  Equipment Type
                </label>
                <Select
                  options={[
                    ...new Set([
                      ...knowledgeLibraryFilterOptions.equipmentTypes.filter(
                        (o) => o !== "Select the Type"
                      ),
                      initialEquipmentType
                    ])
                  ]}
                  value={equipmentType}
                  onChange={(e) => setEquipmentType(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <div className="view-data__filter-group">
                <label className="view-data__filter-label">Discipline</label>
                <Select
                  options={[
                    ...new Set([
                      ...knowledgeLibraryFilterOptions.disciplines.filter(
                        (o) => o !== "Select the Discipline"
                      ),
                      initialDiscipline
                    ])
                  ]}
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  variant="outlined"
                  minWidth={200}
                />
              </div>
              <SearchLoader
                className="view-data__search-btn"
                onClick={handleSearch}
                disabled={isSearchDisabled}
                loading={isLoading}
              >
                Search
              </SearchLoader>
            </div>
          </div>

          {/* Title Section */}
          <div className="view-data__title-section">
            <div className="view-data__title-row">
              <h1 className="view-data__title">
                {kgFilterResponse?.data?.subject || "Untitled Knowledge Item"}
              </h1>
              <span
                className={`view-data__category-badge view-data__category-badge--${(
                  kgFilterResponse?.data?.keywords?.discipline || discipline
                ).toLowerCase()}`}
              >
                {kgFilterResponse?.data?.keywords?.discipline
                  ? toTitleCase(kgFilterResponse.data.keywords.discipline)
                  : discipline}
              </span>
            </div>
            <p className="view-data__subtitle">
              Master technical specification for high-pressure gas injection
              compressors.
            </p>
          </div>

          {/* Tabs */}
          <div className="view-data__tabs">
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Overview" />
              <Tab label="Knowledge Graph" />
            </Tabs>
          </div>

          {/* Tab Content */}
          {activeTab === 0 && (
            <div className="view-data__overview">
              <div className="view-data__main-panel">
                {/* Blueprint Image */}
                <div className="view-data__blueprint">
                  <div className="view-data__blueprint-placeholder">
                    <img
                      src={blueprintPlaceholder}
                      alt="Technical Blueprint"
                      className="view-data__blueprint-image"
                    />
                  </div>
                </div>

                {/* AI Answer */}
                <div className="view-data__specs">
                  {/* <h3 className="view-data__specs-title">AI INSIGHTS</h3> */}
                  <div className="view-data__answer">
                    <p className="view-data__answer-text">
                      {kgFilterResponse?.data?.answer ||
                        "No insights available. Please perform a search to get results."}
                    </p>
                  </div>
                </div>

                {/* Compliance Standards */}
                <div className="view-data__compliance">
                  <h3 className="view-data__compliance-title">
                    COMPLIANCE STANDARDS
                  </h3>
                  <div className="view-data__compliance-tags">
                    <span className="view-data__compliance-tag">API 617</span>
                    <span className="view-data__compliance-tag">ASME VIII</span>
                    <span className="view-data__compliance-tag">ISO 10438</span>
                  </div>
                </div>

                {/* Owner & Last Updated */}
                <div className="view-data__meta-section">
                  <div className="view-data__meta-group">
                    <span className="view-data__meta-label">OWNER</span>
                    <span className="view-data__meta-value">Sam Sequal</span>
                  </div>
                  <div className="view-data__meta-group">
                    <span className="view-data__meta-label">LAST UPDATED</span>
                    <span className="view-data__meta-value">1 hr ago</span>
                  </div>
                </div>

                {/* Related Documents */}
                <div className="view-data__related">
                  <h3 className="view-data__related-title">
                    RELATED DOCUMENTS
                  </h3>
                  <div className="view-data__related-item">
                    <div className="view-data__related-icon">
                      <Description />
                    </div>
                    <div className="view-data__related-info">
                      <span className="view-data__related-name">
                        Safety Protocol v2
                      </span>
                      <span className="view-data__related-size">
                        Document • 4.2 MB
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Properties */}
              <div className="view-data__sidebar">
                {/* <h3 className="view-data__sidebar-title">Place Holders</h3> */}
                <h4 className="view-data__sidebar-section-title">PROPERTIES</h4>
                <div className="view-data__sidebar-properties">
                  <div className="view-data__sidebar-property">
                    <span className="view-data__sidebar-property-label">
                      Discipline
                    </span>
                    <span className="view-data__sidebar-property-value">
                      Mechanical
                    </span>
                  </div>
                  <div className="view-data__sidebar-property">
                    <span className="view-data__sidebar-property-label">
                      Status
                    </span>
                    <span className="view-data__sidebar-property-value view-data__sidebar-property-value--verified">
                      <span className="view-data__status-dot view-data__status-dot--green"></span>
                      Verified
                    </span>
                  </div>
                  <div className="view-data__sidebar-property">
                    <span className="view-data__sidebar-property-label">
                      Criticality
                    </span>
                    <span className="view-data__sidebar-property-value">
                      <span className="view-data__criticality-badge view-data__criticality-badge--high">
                        HIGH
                      </span>
                    </span>
                  </div>
                  <div className="view-data__sidebar-property">
                    <span className="view-data__sidebar-property-label">
                      Last Updated
                    </span>
                    <span className="view-data__sidebar-property-value">
                      2023-10-12
                    </span>
                  </div>
                </div>

                <h4 className="view-data__sidebar-section-title">
                  CONNECTED ENTITIES ({connectedEntities.length})
                </h4>
                <div className="view-data__sidebar-entities">
                  {connectedEntities.map((entity) => (
                    <div key={entity.id} className="view-data__sidebar-entity">
                      <div
                        className="view-data__sidebar-entity-icon"
                        style={{
                          backgroundColor: getEntityBgColor(entity.type),
                          color: getEntityIconColor(entity.type)
                        }}
                      >
                        <EntityIcon type={entity.type} />
                      </div>
                      <div className="view-data__sidebar-entity-info">
                        <span className="view-data__sidebar-entity-name">
                          {entity.name}
                        </span>
                        <span className="view-data__sidebar-entity-type">
                          {entity.category}
                          {/* &bull; {entity.relationship} */}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="view-data__knowledge-graph">
              {nodes.length === 0 ? (
                <div className="view-data__empty-state">
                  <span className="view-data__empty-state-emoji">😞</span>
                  <p className="view-data__empty-state-text">No data found</p>
                </div>
              ) : (
                <>
                  <div className="view-data__graph-area">
                    <GraphCanvas
                      nodes={nodes}
                      edges={edges}
                      selectedNodeId={selectedNodeId}
                      onNodeSelect={setSelectedNodeId}
                    />
                  </div>
                  <div className="view-data__graph-sidebar">
                    <PropertiesPanel
                      properties={
                        selectedNodeId && nodePropertiesMap[selectedNodeId]
                          ? nodePropertiesMap[selectedNodeId]
                          : properties!
                      }
                      connectedEntities={connectedEntities}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewData;
